import { useState } from 'react';
import { Check, AlertCircle, Clock, Layers } from 'lucide-react';

interface SystemError {
  id: string;
  campaign_id?: string;
  campaign_subject?: string;
  created_at: string;
}

interface BulkActionsBarProps {
  errors: SystemError[];
  onBulkAction: (
    action: 'mark_visible' | 'mark_by_campaign' | 'mark_by_timeframe',
    params: any
  ) => Promise<void>;
  disabled?: boolean;
}

interface CampaignStats {
  campaign_id: string;
  campaign_subject: string;
  count: number;
}

export default function BulkActionsBar({ errors, onBulkAction, disabled }: BulkActionsBarProps) {
  const [processing, setProcessing] = useState(false);

  // Analyze errors for campaign stats
  const getCampaignStats = (): CampaignStats | null => {
    const campaignMap = new Map<string, { subject: string; count: number }>();

    errors.forEach((error) => {
      if (error.campaign_id) {
        const existing = campaignMap.get(error.campaign_id);
        if (existing) {
          existing.count++;
        } else {
          campaignMap.set(error.campaign_id, {
            subject: error.campaign_subject || 'Onbekende campagne',
            count: 1,
          });
        }
      }
    });

    if (campaignMap.size === 0) return null;

    // Find most common campaign
    const candidates: CampaignStats[] = [];
    campaignMap.forEach((data, campaign_id) => {
      candidates.push({
        campaign_id,
        campaign_subject: data.subject,
        count: data.count,
      });
    });

    const maxCampaign = candidates.reduce<CampaignStats | null>(
      (best, curr) => (!best || curr.count > best.count ? curr : best),
      null
    );

    // Only show if campaign represents >30% of errors
    if (maxCampaign && maxCampaign.count / errors.length > 0.3) {
      return maxCampaign;
    }

    return null;
  };

  const campaignStats = getCampaignStats();

  const handleBulkMarkVisible = async () => {
    if (errors.length === 0) return;

    const confirmed = window.confirm(
      `Je staat op het punt om ${errors.length} zichtbare email fout(en) te markeren als bekeken.\n\nWeet je het zeker?`
    );

    if (!confirmed) return;

    setProcessing(true);
    try {
      await onBulkAction('mark_visible', {
        error_ids: errors.map((e) => e.id),
      });
    } catch (error) {
      console.error('Bulk action failed:', error);
      alert('Er is een fout opgetreden bij het markeren van emails.');
    } finally {
      setProcessing(false);
    }
  };

  const handleBulkMarkByCampaign = async (campaign_id: string, campaignSubject: string) => {
    const confirmed = window.confirm(
      `Je staat op het punt om ALLE fouten van de campagne "${campaignSubject}" te markeren als bekeken.\n\nDit kan meer emails omvatten dan er momenteel zichtbaar zijn.\n\nWeet je het zeker?`
    );

    if (!confirmed) return;

    setProcessing(true);
    try {
      await onBulkAction('mark_by_campaign', { campaign_id });
    } catch (error) {
      console.error('Bulk action failed:', error);
      alert('Er is een fout opgetreden bij het markeren van emails.');
    } finally {
      setProcessing(false);
    }
  };

  const handleBulkMarkByTimeframe = async () => {
    const hours = window.prompt(
      'Hoeveel uur terug wil je alle fouten markeren als bekeken?\n\nVoorbeeld: voer "1" in voor het laatste uur, "24" voor de laatste 24 uur.',
      '1'
    );

    if (!hours) return;

    const hoursNum = parseInt(hours);
    if (isNaN(hoursNum) || hoursNum <= 0) {
      alert('Voer een geldig aantal uren in (bijvoorbeeld 1, 24, of 168).');
      return;
    }

    const timeframeText =
      hoursNum === 1 ? 'laatste uur' : hoursNum === 24 ? 'laatste 24 uur' : `laatste ${hoursNum} uur`;

    const confirmed = window.confirm(
      `Je staat op het punt om ALLE fouten van de ${timeframeText} te markeren als bekeken.\n\nWeet je het zeker?`
    );

    if (!confirmed) return;

    setProcessing(true);
    try {
      await onBulkAction('mark_by_timeframe', { hours: hoursNum });
    } catch (error) {
      console.error('Bulk action failed:', error);
      alert('Er is een fout opgetreden bij het markeren van emails.');
    } finally {
      setProcessing(false);
    }
  };

  if (errors.length === 0) {
    return null;
  }

  return (
    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
      <div className="flex items-center gap-2 mb-3">
        <Layers className="w-5 h-5 text-blue-600" />
        <h3 className="font-semibold text-gray-900">Bulk Acties</h3>
        <span className="text-sm text-gray-600">
          ({errors.length} {errors.length === 1 ? 'fout' : 'fouten'} geladen)
        </span>
      </div>

      <div className="flex flex-wrap gap-2">
        <button
          onClick={handleBulkMarkVisible}
          disabled={disabled || processing || errors.length === 0}
          className="inline-flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm font-medium"
        >
          <Check className="w-4 h-4" />
          Markeer alle {errors.length} zichtbare
        </button>

        {campaignStats && (
          <button
            onClick={() => handleBulkMarkByCampaign(campaignStats.campaign_id, campaignStats.campaign_subject)}
            disabled={disabled || processing}
            className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm font-medium"
          >
            <AlertCircle className="w-4 h-4" />
            Campagne: "{campaignStats.campaign_subject.substring(0, 30)}
            {campaignStats.campaign_subject.length > 30 ? '...' : ''}"
            <span className="px-2 py-0.5 bg-blue-500 rounded text-xs font-semibold">
              {campaignStats.count}
            </span>
          </button>
        )}

        <button
          onClick={handleBulkMarkByTimeframe}
          disabled={disabled || processing}
          className="inline-flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm font-medium"
        >
          <Clock className="w-4 h-4" />
          Laatste uur/dag...
        </button>
      </div>

      {processing && (
        <div className="mt-3 flex items-center gap-2 text-sm text-gray-600">
          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
          <span>Bezig met markeren...</span>
        </div>
      )}
    </div>
  );
}
